import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, makeStyles, Typography } from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import moment from 'moment';
import * as Yup from 'yup';
import { DateIcon } from '../../../../components/Icon';
import { baseCssBooking } from '../../../BedStatus/components/PopWindows/baseCss';
import DiaLog from '../../../../components/Dialog';
import { i18n } from '../../../../language';
import { classNames } from '../../../../utils';
import { checkSubmitData } from '../../../../utils/check';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiPaper-root': {
      // width: '80%',
      // height: '100%',
      display: 'flex',
      justifyContent: 'center',
      background: 'transparent',
      boxShadow: 'unset',
    },
  },
  root: {
    width: '100%',
    maxWidth: '100rem',
    display: 'flex',
    borderRadius: '0.625rem',
    boxShadow: '0 -2px 21px 0 rgba(30, 56, 140, 0.18)',
    backgroundColor: '#fff',
    background: '#fff',
    height: '100%',
    overflow: 'auto',
    minWidth: '100%',
    flexDirection: 'column',
    padding: '1.857rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignContent: 'center',
    alignItems: 'center',
    gap: '2.857rem',
    // marginBottom: '1.2rem',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  datepicker: {
    '& *': {
      padding: '0',
      margin: '0',
      border: '0',
    },
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
      height: '3.5rem',
      lineHeight: '3.5rem',
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      borderRadius: '0.625rem',
      border: 'solid 1px #e4e7eb',
      backgroundColor: '#fff',
      padding: '0 1.25rem',
      fontSize: '1rem',
    },
    '& .MuiInputBase-input': {
      height: '3.5rem',
      lineHeight: '3.5rem',
    },
    '& :focus': {
      backgroundColor: 'unset',
    },
    '& :before': {
      content: 'none',
    },
    ':before': {
      content: 'none !important',
    },
    '& :after': {
      content: 'none',
    },
  },
  keyboardDatePicker: {
    paddingRight: '0',
  },
  labelError: {
    color: '#f44336',
  },
  xing: {
    color: '#ff0300',
    padding: '0 2px',
    fontSize: '1rem',
    lineHeight: '1rem',
  },
  disabledStyle: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f5f5f5',
    },
  },
}));

export default function PinNoteDialog({ open, closePop, handlePinNote }) {
  const classes = useStyles();
  const baseClasses = baseCssBooking();
  const [pinNoteInfo, setPinNoteInfo] = useState({
    pinExpiryDate: moment().add(7, 'days'),
    isManualUnpin: false,
  });
  const [errorMsg, setErrorMsg] = useState();
  const schema = {
    pinExpiryDate: Yup.date()
      .nullable()
      .when('isManualUnpin', {
        is: (val) => !val,
        then: () => {
          return Yup.date()
            .required(i18n.t('SETTING_IS_A_REQUIRED_FIELD', { value: i18n.t('BEDSTATUS_DATE') }))
            .min(moment().add(1, 'minutes'), '')
            .typeError(i18n.t('BEDSTATUS_INVALID_DATE_FORMAT'));
        },
      }),
    isManualUnpin: Yup.boolean().nullable(),
  };

  const handleConfirm = async () => {
    try {
      await checkSubmitData(Yup.object().shape(schema), pinNoteInfo);
      setErrorMsg(null);
      handlePinNote({
        isManualUnpin: pinNoteInfo.isManualUnpin ? 'Y' : 'N',
        pinExpiryDate: pinNoteInfo.isManualUnpin ? null : pinNoteInfo.pinExpiryDate.format('yyyy-MM-DD HH:00:00'),
      });
      closePop();
    } catch (errorMsg) {
      setErrorMsg(errorMsg);
    }
  };

  const handleChangeExpiryDate = async (event) => {
    try {
      await checkSubmitData(Yup.object().shape({ pinExpiryDate: schema['pinExpiryDate'] }), {
        pinExpiryDate: event,
      });
      setErrorMsg({ ...errorMsg, pinExpiryDate: null });
    } catch (e) {
      setErrorMsg({ ...errorMsg, ...e });
    }
    setPinNoteInfo({ ...pinNoteInfo, pinExpiryDate: event });
  };

  const handleChangeUnpinLater = (event) => {
    const isCheck = event?.target?.checked;
    setPinNoteInfo((prev) => ({
      pinExpiryDate: isCheck ? null : moment().add(7, 'days'),
      isManualUnpin: isCheck,
    }));
  };

  return (
    <DiaLog
      handleClose={closePop}
      handleCancel={closePop}
      handleConfirm={handleConfirm}
      title={i18n.t('EHANDOVER_PIN_NOTE')}
      cancel={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CANCEL')}
      confirm={i18n.t('EHANDOVER_PIN')}
      dialogfootStyle={{ marginTop: '1rem' }}
      open={open}
      className={classes.dialog}
    >
      <Box>
        <Typography className={classNames(baseClasses.labelText, errorMsg?.pinExpiryDate ? classes.labelError : '')}>
          {i18n.t('EHANDOVER_PIN_EXPIRY_DATE')}
          {!pinNoteInfo?.isManualUnpin && <span className={classes.xing}>*</span>}
        </Typography>
        <Box>
          <KeyboardDateTimePicker
            id="e-handover-pinNote-pop-picker"
            autoOk
            variant="inline"
            inputVariant="outlined"
            format="yyyy-MM-DD HH:00:00"
            value={pinNoteInfo?.pinExpiryDate}
            className={classNames(
              classes.datepicker,
              classes.keyboardDatePicker,
              pinNoteInfo?.isManualUnpin && classes?.disabledStyle
            )}
            keyboardIcon={
              <div className={classNames(baseClasses.dateIcon, classes.dateIcon)}>
                <DateIcon />
              </div>
            }
            disabled={pinNoteInfo?.isManualUnpin}
            onChange={handleChangeExpiryDate}
            InputAdornmentProps={{ position: 'end' }}
            minDate={moment().add(1, 'minutes')}
            strictCompareDates={true}
            views={['date', 'hours']}
            disableToolbar={true}
            ampm={false}
            style={{ width: '100%' }}
          />
        </Box>
      </Box>

      <FormControlLabel
        id="bed_transferpop_on_oxygen"
        control={<Checkbox />}
        label={i18n.t('EHANDOVER_UNPIN_LATER')}
        className={baseClasses.checkBox}
        checked={pinNoteInfo?.isManualUnpin}
        onChange={handleChangeUnpinLater}
        style={{ marginTop: '1.714rem' }}
      />
    </DiaLog>
  );
}
